import { useSnackbar } from 'notistack';
import { Dispatch, useCallback, useEffect, useRef, useState } from 'react';
import { UseFormReset } from 'react-hook-form';
import {
  HISTORY_ADDED_DIVISION_CREATED,
  HISTORY_ADDED_DIVISION_DELETED,
  HISTORY_ADDED_DIVISION_UPDATED,
  HISTORY_DISPLAY_LIMIT
} from '../../../../const/Const';
import { TSelectItem } from '../../../../types/common';
import { THistoryData, THistorySearchForm } from '../../../../types/history';
import { createSiteProcessAndSiteTaskEmployeeList } from '../../../../utils';
import { checkResData } from '../../../../utils/common';
import { parseNumber } from '../../../../utils/processAndTaskFormUtils';

export type TProps = {
  id: number; // 取得するデータのid
  itemList: Array<TSelectItem>; //選択項目リスト
  apiHistory: (
    id: number,
    choiceItem?: number[],
    userId?: number,
    page?: number,
    displayLimit?: number
  ) => Promise<any>; // 履歴取得用のapi。idを渡して検索を行うapi。
  createHistoryText: (apiResponseHistory: any) => string; // apiから取得した履歴デーかから表示する更新内容を作成する。
  isOpen: boolean;
  setIsOpen: Dispatch<boolean>;
  reset: UseFormReset<THistorySearchForm>;
};

export const useHooks = (props: TProps) => {
  const {
    id,
    itemList,
    apiHistory,
    createHistoryText,
    isOpen,
    setIsOpen,
    reset
  } = props;
  const [historyDataList, setHistoryDataList] = useState<Array<THistoryData>>(
    []
  );

  // snackbar
  const { enqueueSnackbar } = useSnackbar();

  // apiリクエストする検索条件。ページングがあるため検索ボタンが押された時の状態を保持
  const [searchParams, setSearchParams] = useState<{
    userId?: number;
    choiceItem: Array<number>;
    pageIndex: number;
    hasMorePage: boolean;
  }>({
    userId: 0,
    choiceItem: [],
    pageIndex: 1,
    hasMorePage: true
  });

  // 検索クリック処理。検索用のstateを更新し実際の検索処理はuseEffectで行う
  const handleSearchClick = (form: THistorySearchForm) => {
    const choiceItem = itemList
      .filter(item => form.choice_item.includes(item.label))
      .map(item => item.value);

    setSearchParams({
      userId: parseNumber(form.user_id) || undefined,
      choiceItem: choiceItem,
      pageIndex: 1,
      hasMorePage: true
    });
  };

  // 検索＆初回ローディング
  const [isLoading, setIsLoading] = useState(false);
  // スクロールローディング
  const [isLoadingPage, setIsLoadingPage] = useState(false);

  // add_divisionに合わせたテキストを作成
  const convertToAddedDivisionText = (id: number): string => {
    switch (id) {
      case HISTORY_ADDED_DIVISION_CREATED:
        return 'を新規作成しました。';
      case HISTORY_ADDED_DIVISION_UPDATED:
        return 'を更新しました。';
      case HISTORY_ADDED_DIVISION_DELETED:
        return 'を削除しました。';
      default:
        return '';
    }
  };

  // データ読み込み＆画面に描画するデータ作成処理
  const fetchHistoryDiff = () => {
    if (id > 0) {
      // ローディング開始
      if (searchParams.pageIndex === 1) {
        setIsLoading(true);
      } else {
        setIsLoadingPage(true);
      }

      apiHistory(
        id,
        searchParams.choiceItem,
        searchParams.userId,
        searchParams.pageIndex,
        HISTORY_DISPLAY_LIMIT
      ).then(resData => {
        checkResData(resData, enqueueSnackbar).then(data => {
          if (data) {
            // 1ページ目(検索ボタン押下による検索)の場合はスクロール位置をトップに戻す
            if (searchParams.pageIndex === 1) {
              const hisrotyModalContent = document.getElementById(
                'hisrotyModalContent'
              );
              if (hisrotyModalContent) {
                hisrotyModalContent.scrollTo(0, 0);
              }
            }
            const newHistoryData = data.data.map((history: any) => {
              return {
                id: history.id,
                user: history.added_user_name,
                addedAt: history.added_at,
                addedDivision: history.added_division,
                text: `${createHistoryText(
                  history
                )}${convertToAddedDivisionText(history.added_division)}`
              };
            });

            if (searchParams.pageIndex === 1) {
              setHistoryDataList(newHistoryData);
            } else {
              setHistoryDataList(prev => [...prev, ...newHistoryData]);
            }

            // スクロール設定を更新
            setSearchParams(prevState => {
              const hasMore = data.page_count > prevState.pageIndex;
              if (prevState.hasMorePage !== hasMore) {
                return {
                  ...prevState,
                  hasMorePage: hasMore
                };
              }
              return prevState;
            });
          }
          // ローディング終了
          setIsLoading(false);
          setIsLoadingPage(false);
        });
      });
    }
  };

  // 更新者リスト
  const [userList, setUserList] = useState<TSelectItem[]>([
    { value: 0, label: '更新者' }
  ]);

  // スクロール設定
  const observer = useRef<IntersectionObserver>();
  const lastElementRef = useCallback((node: any) => {
    if (observer === undefined) {
      return;
    }
    if (observer.current) {
      observer.current.disconnect();
    }
    observer.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting) {
        // 現在のページを更新
        setSearchParams(prevState => ({
          ...prevState,
          pageIndex: prevState.pageIndex + 1
        }));
        observer.current?.disconnect();
      }
    });
    if (node) {
      observer.current.observe(node);
    }
  }, []);

  // モーダルオープン時の処理
  useEffect(() => {
    if (isOpen) {
      reset();
      // 更新者選択プルダウン用のリスト取得
      createSiteProcessAndSiteTaskEmployeeList(
        id,
        enqueueSnackbar,
        '更新者'
      ).then(userList => {
        setUserList(userList);
      });
      // 初期値をセット
      setSearchParams({
        userId: undefined,
        choiceItem: [],
        pageIndex: 1,
        hasMorePage: true
      });
    } else {
      setHistoryDataList([]);
    }
  }, [isOpen]);

  // 検索時とページング時
  useEffect(() => {
    if (isOpen && searchParams.hasMorePage) {
      fetchHistoryDiff(); // 履歴のデータを取得
    }
  }, [searchParams]);

  const onClickCloseButton = () => {
    setIsOpen(false);
  };

  return {
    historyDataList,
    lastElementRef,
    onClickCloseButton,
    userList,
    handleSearchClick,
    isLoading,
    isLoadingPage
  };
};
